import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  Map, Sparkles, ChevronRight, X, Globe, CheckCircle, 
  Camera, Users, Lightbulb, DollarSign 
} from "lucide-react";

const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Congratulations on your first achievement!",
    description: "You've completed your first bucket list item! Let's show you some features to make your journey even better.",
    icon: CheckCircle,
    highlight: null,
  },
  {
    id: "world-map",
    title: "Track Your Adventures",
    description: "See all the countries you've visited on your personal World Map. Watch it fill up as you complete more adventures!",
    icon: Map,
    highlight: "[data-testid='nav-map-btn']",
    action: "openWorldMap",
  },
  {
    id: "suggestions",
    title: "Get AI Trip Ideas",
    description: "Need inspiration? Our AI suggests personalized trips based on your bucket list and preferences.",
    icon: Lightbulb,
    highlight: "[data-testid='nav-suggestions-btn']",
  },
  {
    id: "budget",
    title: "Track Your Budget",
    description: "Keep track of expenses for each trip. Set estimates, log actual costs, and stay within budget.",
    icon: DollarSign,
    highlight: "[data-testid='nav-budget-btn']",
  },
  {
    id: "photos",
    title: "Capture Memories",
    description: "Add photos to your completed items and create a beautiful scrapbook of your adventures.",
    icon: Camera,
    highlight: null,
  },
  {
    id: "complete",
    title: "You're All Set!",
    description: "Now go explore your World Map and start planning your next adventure. The world is waiting!",
    icon: Globe,
    highlight: null,
    action: "finish",
  },
];

const STORAGE_KEY = "duolist_tour_completed";
const FIRST_COMPLETION_KEY = "duolist_first_completion_shown";

export const OnboardingTour = ({ isOpen, onClose, onOpenWorldMap, stats }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [isOpen]);

  const handleNext = () => {
    const step = TOUR_STEPS[currentStep];
    
    if (step.action === "openWorldMap" && onOpenWorldMap) {
      onOpenWorldMap();
    }
    
    if (currentStep < TOUR_STEPS.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 200);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    localStorage.setItem(FIRST_COMPLETION_KEY, "true");
    onClose();
  };

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Tour Card */}
      <div 
        className={`relative bg-card rounded-3xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transform transition-all duration-300 ${
          isAnimating ? "scale-95 opacity-0" : "scale-100 opacity-100"
        }`}
      >
        {/* Progress Bar */}
        <div className="h-1 bg-charcoal/10">
          <div 
            className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
            style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-charcoal/5 transition-colors z-10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Content */}
        <div className="p-8 pt-12">
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center ${
            isFirstStep 
              ? "bg-gradient-to-br from-secondary to-secondary/70" 
              : step.id === "world-map"
                ? "bg-gradient-to-br from-primary to-primary/70"
                : "bg-gradient-to-br from-charcoal/10 to-charcoal/5"
          }`}>
            <StepIcon className={`w-10 h-10 ${
              isFirstStep || step.id === "world-map" ? "text-white" : "text-charcoal"
            }`} />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-serif font-semibold text-charcoal text-center mb-3">
            {step.title}
          </h2>

          {/* Description */}
          <p className="text-muted-foreground text-center mb-8 leading-relaxed">
            {step.description}
          </p>

          {/* Stats Preview (on first step) */}
          {isFirstStep && stats && (
            <div className="flex justify-center gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{stats.completed_items || 1}</div>
                <div className="text-xs text-muted-foreground">Completed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-secondary">{stats.total_items || 1}</div>
                <div className="text-xs text-muted-foreground">Total Dreams</div>
              </div>
            </div>
          )}

          {/* World Map Preview (on map step) */}
          {step.id === "world-map" && (
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary/80" />
                  <span className="text-xs text-muted-foreground">Visited</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-400/80" />
                  <span className="text-xs text-muted-foreground">Planned</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {!isFirstStep && !isLastStep && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleSkip}
                className="flex-1 text-muted-foreground hover:text-charcoal"
              >
                Skip Tour
              </Button>
            )}
            <Button
              type="button"
              onClick={handleNext}
              className={`flex-1 rounded-full btn-press ${
                isLastStep 
                  ? "bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90" 
                  : "bg-primary text-white hover:bg-primary/90"
              }`}
            >
              {isLastStep ? (
                <>
                  <Map className="w-4 h-4 mr-2" />
                  Open World Map
                </>
              ) : isFirstStep ? (
                <>
                  Show Me
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Step Indicators */}
          <div className="flex justify-center gap-1.5 mt-6">
            {TOUR_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? "w-6 bg-primary" 
                    : idx < currentStep 
                      ? "w-1.5 bg-secondary" 
                      : "w-1.5 bg-charcoal/20"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to manage tour state
export const useOnboardingTour = (stats) => {
  const [showTour, setShowTour] = useState(false);

  const checkAndShowTour = (wasFirstCompletion) => {
    // Only show if this was the first completion AND tour hasn't been shown before
    const tourCompleted = localStorage.getItem(STORAGE_KEY);
    const firstCompletionShown = localStorage.getItem(FIRST_COMPLETION_KEY);
    
    if (wasFirstCompletion && !tourCompleted && !firstCompletionShown) {
      // Delay slightly to let the completion celebration happen first
      setTimeout(() => {
        setShowTour(true);
      }, 1500);
    }
  };

  const closeTour = () => {
    setShowTour(false);
    localStorage.setItem(STORAGE_KEY, "true");
    localStorage.setItem(FIRST_COMPLETION_KEY, "true");
  };

  return {
    showTour,
    setShowTour,
    checkAndShowTour,
    closeTour,
  };
};

export default OnboardingTour;
